import { useCallback } from 'react';
import { useFileUpload } from '@/lib/services/files';
import { attachMatterDocument } from '@/lib/services/redemption/pms-import';
import type { AddDocumentTarget, AddDocumentUploadContext } from './types';

/** The per-type association ids a requirement document lands with (shared by upload + link-existing). */
function associationsOf(target: AddDocumentTarget) {
  return {
    ...(target.contactId != null && { contactId: target.contactId }),
    ...(target.institutionId != null && {
      institutionId: target.institutionId
    }),
    ...(target.institutionFormId != null && {
      institutionFormId: target.institutionFormId
    }),
    ...(target.signatoryContactId != null && {
      signatoryContactId: target.signatoryContactId
    }),
    ...(target.inventoryItemId != null && {
      inventoryItemId: target.inventoryItemId
    }),
    ...(target.attachmentId != null && { attachmentId: target.attachmentId })
  };
}

/**
 * The matter-requirement landing actions for {@link AddDocument}'s `target`
 * mode — the single client call-site that ADOC-4 (LF-6190) re-points at the
 * unified `source`-discriminated endpoint.
 *
 * - **upload** copies a fresh file onto the matter as `target.fileType` with the
 *   target's associations (the standard presign → PUT → complete path, already
 *   on the `uploadFileToUrl` transport).
 * - **pickExisting** links an *existing* matter file into the requirement via the
 *   unified attach endpoint with `source: 'existing'` (copy + re-type, source
 *   untouched).
 *
 * Both resolve the resulting Vale fileId(s). Returns empty actions when no
 * `target` is supplied (callback-mode surfaces like the beneficiary email
 * attachments supply their own actions instead).
 */
export function useMatterRequirementAttach(
  matterId: number,
  target?: AddDocumentTarget
): {
  upload?: (files: File[], ctx: AddDocumentUploadContext) => Promise<number[]>;
  pickExisting?: (fileIds: number[]) => Promise<number[]>;
} {
  const { uploadFile } = useFileUpload(matterId);

  const upload = useCallback(
    async (files: File[], ctx: AddDocumentUploadContext): Promise<number[]> => {
      if (!target) return [];
      const ids: number[] = [];
      for (const file of files) {
        const uploaded = await uploadFile(file, target.fileType, {
          displayName: target.displayName ?? file.name,
          ...associationsOf(target),
          onProgress: ctx.onProgress,
          signal: ctx.signal
        });
        ids.push(uploaded.id);
      }
      return ids;
    },
    [uploadFile, target]
  );

  const pickExisting = useCallback(
    async (fileIds: number[]): Promise<number[]> => {
      if (!target) return [];
      const ids: number[] = [];
      for (const fileId of fileIds) {
        const res = await attachMatterDocument(matterId, {
          source: 'existing',
          fileId,
          fileType: target.fileType,
          ...associationsOf(target)
        });
        ids.push(res.fileId);
      }
      return ids;
    },
    [matterId, target]
  );

  if (!target) return {};
  return { upload, pickExisting };
}
